import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, Crown } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { fetchCheckoutStatus } from "../lib/billing";
import { useEntitlement } from "../contexts/EntitlementContext";
import { Confetti } from "../components/Confetti";

const MAX_POLLS = 20;
const POLL_MS = 2500;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh: refreshEntitlement } = useEntitlement();
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [errorMsg, setErrorMsg] = useState("");
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard", { replace: true });
      return;
    }

    let timeoutId = null;

    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const data = await fetchCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          stoppedRef.current = true;
          setState("paid");
          refreshEntitlement();
          return;
        }
        if (data.status === "expired") {
          stoppedRef.current = true;
          setState("expired");
          return;
        }
        // Otherwise: still pending — fall through to retry
      } catch (e) {
        // Don't immediately fail on transient errors — keep retrying.
        // Only after exhausting MAX_POLLS we show the error UI.
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_POLLS) {
        stoppedRef.current = true;
        setState("error");
        setErrorMsg("El pago aún se está procesando. Si tu tarjeta fue cobrada, ve a 'Mi Suscripción' y toca 'Verificar pago'.");
        return;
      }
      timeoutId = setTimeout(poll, POLL_MS);
    };

    poll();

    return () => {
      stoppedRef.current = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <PageShell>
      {state === "paid" && <Confetti pieces={120} />}
      <div className="max-w-lg mx-auto mt-10 sm:mt-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-[2.5rem] border-8 border-white shadow-[0_25px_50px_-20px_rgba(0,0,0,0.15)] p-8 sm:p-10 text-center"
        >
          {state === "polling" && (
            <>
              <Loader2 className="w-16 h-16 sm:w-20 sm:h-20 mx-auto text-[#FF9500] animate-spin mb-4" strokeWidth={2.5} />
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2">
                Procesando pago…
              </h1>
              <p className="text-base text-[#5C5C60]">Esto toma unos segundos.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", bounce: 0.6 }}
                className="inline-flex w-24 h-24 rounded-3xl bg-gradient-to-br from-[#FFD700] to-[#FF9500] items-center justify-center mb-4 shadow-lg"
              >
                <Crown className="w-14 h-14 text-white" strokeWidth={2.5} />
              </motion.div>
              <h1
                className="font-heading text-4xl sm:text-5xl font-bold text-[#1A6B2F] mb-2"
                data-testid="billing-success-title"
              >
                ¡Bienvenido a Premium! 🎉
              </h1>
              <p className="text-base text-[#5C5C60] mb-6">
                Tu suscripción está activa. ¡Ahora puedes practicar sin límites!
              </p>
              <ClayButton
                onClick={() => navigate("/dashboard", { replace: true })}
                size="lg"
                className="w-full"
                data-testid="billing-success-continue-btn"
              >
                Empezar a practicar
              </ClayButton>
            </>
          )}
          {(state === "expired" || state === "error") && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-[#FF6B6B] mb-4" strokeWidth={2.5} />
              <h1 className="font-heading text-3xl font-bold text-[#803535] mb-2">
                {state === "expired" ? "Sesión expirada" : "Pago en proceso"}
              </h1>
              <p className="text-base text-[#5C5C60] mb-6">{errorMsg}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <ClayButton
                  variant="secondary"
                  onClick={() => navigate("/dashboard", { replace: true })}
                  data-testid="billing-success-home-btn"
                >
                  Volver al inicio
                </ClayButton>
                <ClayButton
                  onClick={() => navigate("/mi-suscripcion", { replace: true })}
                  data-testid="billing-success-verify-btn"
                >
                  Verificar mi pago
                </ClayButton>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </PageShell>
  );
}

// suppress unused import warning for icons that may not appear in all states
// eslint-disable-next-line no-unused-vars
const _icons = CheckCircle2;
